/**
 * <dialed-hours-badge> shows whether support is open right now, from the "Support hours"
 * theme settings (passed as data attributes by snippets/dialed-hours-badge.liquid).
 *
 * Renders server-side as neutral text, then this sets `data-state="open|closed"` and swaps in
 * the open or closed label. Rechecks every minute.
 */

const DAYS = {
  mon_fri: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  mon_sat: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  every_day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

/**
 * @param {{ timezone: string, days: string, open: number, close: number }} hours
 * @param {Date} [now]
 */
export function isOpen({ timezone, days, open, close }, now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24;
  const openDays = DAYS[/** @type {keyof typeof DAYS} */ (days)] ?? DAYS.mon_fri;
  return openDays.includes(weekday) && hour >= open && hour < close;
}

class DialedHoursBadge extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), 60_000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #update() {
    const { timezone = 'America/Chicago', days = 'mon_fri', opens = '8', closes = '17' } = this.dataset;
    let openNow;
    try {
      openNow = isOpen({ timezone, days, open: Number(opens), close: Number(closes) });
    } catch {
      return;
    }
    this.dataset.state = openNow ? 'open' : 'closed';
    const label = this.querySelector('[data-hours-label]');
    if (label) label.textContent = (openNow ? this.dataset.openLabel : this.dataset.closedLabel) ?? '';
  }
}

if (!customElements.get('dialed-hours-badge')) customElements.define('dialed-hours-badge', DialedHoursBadge);
